import { ValidationErrors } from 'acscp/api/models/errorDetails';

/**
 * Creates a deep clone of the source ValidationErrors object.
 */
export function cloneValidationErrors(source: ValidationErrors): ValidationErrors {
    if (Object.keys(source).length === 0) {
        return {};
    }

    return combineValidationErrors(source, {});
}

/**
 * Combines the provided ValidationErrors objects into a new one containing all error messages for all keys.
 * Note: this function does not deduplicate error messages.
 *
 * Example:
 * a {
 *   password: [
 *     {message: 'Too short'}
 *   ],
 *   email: [
 *     {message: 'Not valid'}
 *   ]
 * }
 * b {
 *   password: [
 *     {message: 'Too short'},
 *     {message: 'Can not be equal to username'}
 *   ]
 * }
 * combineValidationErrors(a, b) {
 *   password: [
 *     {message: 'Too short'},
 *     {message: 'Too short'},
 *     {message: 'Can not be equal to username'}
 *   ],
 *   email: [
 *     {message: 'Not valid'}
 *   ]
 * }
 */
export function combineValidationErrors(a: ValidationErrors, b: ValidationErrors): ValidationErrors {
    const result = cloneValidationErrors(b);

    Object.keys(a)
        .reduce(
            (acc, key) => {
                acc[key] = acc[key] || [];
                acc[key] = acc[key].concat(a[key]);
                return acc;
            },
            result
        );

    return result;
}

/**
 * Creates a new ValidationErrors object containing all errors from the input object
 * plus the new error message entered under the specified property.
 *
 * Example:
 * target {
 *   username: [
 *     {message: 'Can not be empty'}
 *   ]
 * }
 * failedProperty 'username'
 * errorMessage 'Can not contain tabulation symbols'
 * addValidationError(target, failedProperty, errorMessage) {
 *   username: [
 *     {message: 'Can not be empty'},
 *     {message: 'Can not contain tabulation symbols'}
 *   ]
 * }
 */
export function addValidationError(
    target: ValidationErrors,
    failedProperty: string,
    errorMessage: string): ValidationErrors {

    const result = cloneValidationErrors(target);

    result[failedProperty] = result[failedProperty] || [];
    result[failedProperty].push({message: errorMessage});

    return result;
}
